import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, File } from 'lucide-react'
import toast from 'react-hot-toast'
import mammoth from 'mammoth'
import { jsPDF } from 'jspdf'
import { renderAsync } from 'docx-preview'
import html2canvas from 'html2canvas'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'
import { useSEO, createToolPageStructuredData, createBreadcrumbStructuredData, generateKeywords } from '../hooks/useSEO'

interface ConversionOptions {
  preserveFormatting: boolean
  includeImages: boolean
  includeHeaders: boolean
  includeFooters: boolean
  pageMargins: 'narrow' | 'normal' | 'wide'
  fontSize: number
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  pdfBlob?: Blob
}

const WordToPdf: React.FC = () => {
  // SEO for Word to PDF converter page
  useSEO({
    title: 'Word to PDF Converter - Convert DOC, DOCX to PDF Online Free | SnapTools',
    description: 'Convert Word documents to PDF online free. Support DOC, DOCX files. Professional Word to PDF converter with formatting preservation. Fast, secure, no registration required. Convert documents to PDF instantly.',
    keywords: generateKeywords('word', [
      'Word to PDF converter online free',
      'convert DOC to PDF',
      'convert DOCX to PDF',
      'Word document to PDF',
      'document to PDF converter',
      'DOC converter online',
      'DOCX converter free',
      'Word file converter',
      'Word export PDF',
      'document PDF converter',
      'convert Word files',
      'DOC to PDF online',
      'DOCX to PDF online',
      'Word PDF converter',
      'document to PDF tool',
      'convert Word documents',
      'Word document converter',
      'document export tool',
      'Word converter online',
      'Word text converter',
      'convert Word format',
      'DOC file converter',
      'DOCX file converter',
      'Word online converter',
      'document PDF generator',
      'Word PDF creator',
      'convert Word free',
      'DOC to PDF free',
      'DOCX to PDF free',
      'Word converter tool',
      'document converter online',
      'Word file converter',
      'Word to PDF online',
      'convert Word document',
      'DOC converter tool',
      'DOCX converter tool',
      'Word PDF export',
      'document PDF conversion',
      'Word PDF conversion'
    ]),
    canonical: 'https://snap-tools.online/convert/word-to-pdf',
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        createToolPageStructuredData(
          'Word to PDF Converter',
          'Convert Word documents (DOC, DOCX) to PDF format online. Free, fast, and secure conversion tool with formatting preservation.',
          'https://snap-tools.online/convert/word-to-pdf'
        ),
        createBreadcrumbStructuredData([
          { name: 'Home', url: 'https://snap-tools.online/' },
          { name: 'Document Converters', url: 'https://snap-tools.online/' },
          { name: 'Word to PDF', url: 'https://snap-tools.online/convert/word-to-pdf' }
        ])
      ]
    }
  })

  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    preserveFormatting: true,
    includeImages: true,
    includeHeaders: true,
    includeFooters: true,
    pageMargins: 'normal',
    fontSize: 12
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const wordFiles = acceptedFiles.filter(file => 
      file.type.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') ||
      file.type.includes('application/msword') ||
      file.name.toLowerCase().endsWith('.docx') ||
      file.name.toLowerCase().endsWith('.doc')
    )

    if (wordFiles.length === 0) {
      toast.error('Please select valid Word documents (.doc, .docx)')
      return
    }

    const newFiles: ProcessedFile[] = wordFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${wordFiles.length} Word document(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 20 } : f
        ))

        // Read Word document content
        const arrayBuffer = await file.originalFile.arrayBuffer()
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 30 } : f
        ))

        // Create temporary container for rendering
        const tempContainer = document.createElement('div')
        tempContainer.style.width = '800px'
        tempContainer.style.backgroundColor = 'white'
        tempContainer.style.padding = `${options.pageMargins === 'narrow' ? '10' : options.pageMargins === 'wide' ? '30' : '20'}px`
        tempContainer.style.fontFamily = 'Arial, sans-serif'
        tempContainer.style.fontSize = `${options.fontSize}px`
        tempContainer.style.position = 'absolute'
        tempContainer.style.left = '-9999px'
        tempContainer.style.top = '0'
        
        document.body.appendChild(tempContainer)

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 50 } : f
        ))

        try {
          // Try docx-preview first for better formatting
          await renderAsync(arrayBuffer, tempContainer)
        } catch (docxError) {
          // Fallback to mammoth if docx-preview fails
          console.warn('DOCX preview failed, falling back to mammoth:', docxError)
          const result = await mammoth.convertToHtml({ arrayBuffer })
          tempContainer.innerHTML = result.value
        }

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 70 } : f
        ))

        // Convert the rendered content to canvas
        const canvas = await html2canvas(tempContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: tempContainer.scrollWidth,
          height: tempContainer.scrollHeight
        })

        // Remove temporary container
        document.body.removeChild(tempContainer)

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 85 } : f
        ))

        // Create PDF from canvas
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height]
        })

        const imgData = canvas.toDataURL('image/jpeg', 0.95)
        pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height)

        const pdfBlob = pdf.output('blob')

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, pdfBlob: pdfBlob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('Word to PDF conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.pdfBlob) return

    const url = URL.createObjectURL(file.pdfBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            Word to PDF Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert Microsoft Word documents to PDF with preserved formatting, images, and layout.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.wordToPdf?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Conversion Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="preserveFormatting"
                  checked={options.preserveFormatting}
                  onChange={(e) => setOptions(prev => ({ ...prev, preserveFormatting: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="preserveFormatting" className="text-sm font-medium text-gray-700">
                  Preserve original formatting
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeImages"
                  checked={options.includeImages}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeImages: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeImages" className="text-sm font-medium text-gray-700">
                  Include images and graphics
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeHeaders"
                  checked={options.includeHeaders}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeHeaders: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeHeaders" className="text-sm font-medium text-gray-700">
                  Include headers
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeFooters"
                  checked={options.includeFooters}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeFooters: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeFooters" className="text-sm font-medium text-gray-700">
                  Include footers
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Page Margins
                </label>
                <select
                  value={options.pageMargins}
                  onChange={(e) => setOptions(prev => ({ ...prev, pageMargins: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="narrow">Narrow (0.5 inch)</option>
                  <option value="normal">Normal (1 inch)</option>
                  <option value="wide">Wide (1.5 inch)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font Size: {options.fontSize}pt
                </label>
                <input
                  type="range"
                  min="8"
                  max="24"
                  value={options.fontSize}
                  onChange={(e) => setOptions(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #8438ff 0%, #8438ff ${((options.fontSize - 8) / (24 - 8)) * 100}%, #e5e7eb ${((options.fontSize - 8) / (24 - 8)) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your Word files here' : 'Drag & drop Word documents here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports DOC, DOCX • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.wordToPdf?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to PDF'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <File className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download PDF
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 'Converting...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.wordToPdf?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default WordToPdf
